import React, { useState } from "react";
import API from "../services/api";
import "../App.css";
import "./Settings.css";

const Settings = ({ onUsernameUpdate }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState(""); // 'Username', 'Password', or 'Delete'
  const [isUpdating, setIsUpdating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "dark");
  const [formData, setFormData] = useState({
    username: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [validationErrors, setValidationErrors] = useState({
    username: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    general: ""
  });
  const [successMessage, setSuccessMessage] = useState("");
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

  const token = localStorage.getItem("token");

  // Fetch user settings like 2FA on mount
  React.useEffect(() => {
    let mounted = true;
    const fetchSettings = async () => {
      try {
        const res = await API.get('/auth/user/info', { headers: { Authorization: `Bearer ${token}` } });
        if (!mounted) return;
        setTwoFactorEnabled(Boolean(res.data.two_factor_enabled));
      } catch (err) {
        console.error('Failed to fetch user settings', err);
      }
    };
    if (token) fetchSettings();
    return () => { mounted = false; };
  }, [token]);

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
    document.documentElement.setAttribute("data-theme", newTheme);
  };

  const openModal = (type) => {
    setModalType(type);
    setIsModalOpen(true);
    setSuccessMessage("");
    setIsConfirmOpen(false);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({ username: "", currentPassword: "", newPassword: "", confirmPassword: "" });
    setValidationErrors({ username: "", currentPassword: "", newPassword: "", confirmPassword: "", general: "" });
    setSuccessMessage("");
    setIsConfirmOpen(false);
  };

  const validatePassword = (password) => {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
  };

  const validateUsername = (username) => {
    if (username.length < 3) {
      return "Username must be at least 3 characters long";
    }
    if (username.length > 50) {
      return "Username cannot exceed 50 characters";
    }
    if (username.includes(' ')) {
      return "Username must not contain spaces";
    }
    if (!/^[a-zA-Z0-9_]*$/.test(username)) {
      return "Username can only contain letters, numbers, and underscores";
    }
    return "";
  };

  const handleInputChange = (field, value) => {
    setFormData({...formData, [field]: value});
    
    // Clear previous errors and success message for this field
    setValidationErrors({...validationErrors, [field]: "", general: ""});
    setSuccessMessage("");
    
    // Real-time validation for username field
    if (modalType === "Username" && field === "username") {
      const usernameError = validateUsername(value);
      if (usernameError) {
        setValidationErrors(prev => ({
          ...prev, 
          username: usernameError
        }));
      }
    }
    
    // Real-time validation for password fields
    if (modalType === "Password") {
      if (field === "newPassword") {
        if (value && !validatePassword(value)) {
          setValidationErrors(prev => ({
            ...prev, 
            newPassword: "Password must be at least 8 characters with uppercase, lowercase, number, and special character"
          }));
        }
      } else if (field === "confirmPassword") {
        if (value && value !== formData.newPassword) {
          setValidationErrors(prev => ({
            ...prev, 
            confirmPassword: "Passwords do not match"
          }));
        }
      } else if (field === "newPassword" && formData.confirmPassword) {
        if (value !== formData.confirmPassword) {
          setValidationErrors(prev => ({
            ...prev, 
            confirmPassword: "Passwords do not match"
          }));
        } else {
          setValidationErrors(prev => ({
            ...prev, 
            confirmPassword: ""
          }));
        }
      }
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setIsUpdating(true);

    try {
      // Client-side validations with inline error display
      let hasErrors = false;
      const newErrors = { username: "", currentPassword: "", newPassword: "", confirmPassword: "", general: "" };

      if (modalType === "Username") {
        const usernameError = validateUsername(formData.username);
        if (usernameError) {
          newErrors.username = usernameError;
          hasErrors = true;
        }

        if (hasErrors) {
          setValidationErrors(newErrors);
          return;
        }
      }

      if (modalType === "Password") {
        const { currentPassword, newPassword, confirmPassword } = formData;

        if (newPassword !== confirmPassword) {
          newErrors.confirmPassword = "Passwords do not match";
          hasErrors = true;
        }

        if (currentPassword === newPassword) {
          newErrors.general = "The new password must be different from the current password";
          hasErrors = true;
        }

        if (!validatePassword(newPassword)) {
          newErrors.newPassword = "Password must be at least 8 characters with uppercase, lowercase, number, and special character";
          hasErrors = true;
        }

        if (hasErrors) {
          setValidationErrors(newErrors);
          return;
        }
      }

      // Clear any existing errors before API call
      setValidationErrors({ username: "", currentPassword: "", newPassword: "", confirmPassword: "", general: "" });

      const isUsernameModal = modalType === "Username";
      const isPasswordModal = modalType === "Password";
      const isDeleteModal = modalType === "Delete";

      if (isDeleteModal && !isConfirmOpen) {
        // Ask for final confirmation before deleting
        setIsConfirmOpen(true);
        setIsUpdating(false);
        return;
      }

      const endpoint = isUsernameModal
        ? "/settings/update-username"
        : isPasswordModal
          ? "/settings/change-password"
          : "/settings/delete-account";

      const payload = isUsernameModal
        ? { username: formData.username }
        : isPasswordModal
          ? { current_password: formData.currentPassword, new_password: formData.newPassword }
          : { current_password: formData.currentPassword };

      const headers = { Authorization: `Bearer ${token}` };
      const res = isDeleteModal
        ? await API.delete(endpoint, { data: payload, headers })
        : await API.put(endpoint, payload, { headers });

      // Clear form fields immediately and show success message
      setFormData({ username: "", currentPassword: "", newPassword: "", confirmPassword: "" });
      const fallbackMessage = isDeleteModal ? "Account scheduled for deletion in 24 hours" : `${modalType} has been updated successfully`;
      setSuccessMessage(res.data.message || fallbackMessage);

      // Update username in parent component and localStorage if username was changed
      if (isUsernameModal && onUsernameUpdate) {
        localStorage.setItem("username", formData.username);
        onUsernameUpdate(formData.username);
      }

      if (isDeleteModal) {
        setTimeout(() => {
          try {
            localStorage.removeItem("token");
            localStorage.removeItem("username");
            // remove agent session keys
            try { Object.keys(localStorage).forEach(k => { if (typeof k === 'string' && (k.startsWith('agent_sessions') || k.startsWith('agent_active_session'))) localStorage.removeItem(k); }); } catch (e) {}
          } catch (e) {}
          window.location.href = "/login";
        }, 1200);
        return;
      }

      // Close modal after showing success message
      setTimeout(() => {
        closeModal();
      }, 2000);
    } catch (err) {
      console.error("Update Error:", err);
      // Show API errors as inline messages
      const errorMessage = err.response?.data?.detail || `An error occurred while updating the ${modalType.toLowerCase()}`;
      setValidationErrors(prev => ({...prev, general: errorMessage}));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleConfirmDelete = async () => {
    setIsUpdating(true);
    try {
      setValidationErrors({ username: "", currentPassword: "", newPassword: "", confirmPassword: "", general: "" });

      const headers = { Authorization: `Bearer ${token}` };
      const payload = { current_password: formData.currentPassword };
      const res = await API.delete("/settings/delete-account", { data: payload, headers });

      setFormData({ username: "", currentPassword: "", newPassword: "", confirmPassword: "" });
      setSuccessMessage(res.data.message || "Account scheduled for deletion in 24 hours");

      setIsConfirmOpen(false);
      setIsModalOpen(false);

      setTimeout(() => {
        try {
          localStorage.removeItem("token");
          localStorage.removeItem("username");
          try { Object.keys(localStorage).forEach(k => { if (typeof k === 'string' && (k.startsWith('agent_sessions') || k.startsWith('agent_active_session'))) localStorage.removeItem(k); }); } catch (e) {}
        } catch (e) {}
        window.location.href = "/login";
      }, 1200);
    } catch (err) {
      console.error("Delete Error:", err);
      const errorMessage = err.response?.data?.detail || "An error occurred while deleting the account";
      setValidationErrors(prev => ({...prev, general: errorMessage}));
      setIsConfirmOpen(false);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDownloadAll = async () => {
    setIsExporting(true);
    setValidationErrors(prev => ({ ...prev, general: "" }));
    setSuccessMessage("");
    try {
      const res = await API.get('/tasks');
      const tasks = Array.isArray(res.data) ? res.data : [];

      const esc = (v) => {
        if (v === null || v === undefined) return "";
        return String(v)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/\n/g, '<br />');
      };

      const fmtDate = (d) => {
        if (!d) return '—';
        try {
          return new Date(d).toLocaleString();
        } catch (e) {
          return esc(d);
        }
      };

      const tasksHtml = tasks.map((t, idx) => {
        const status = t.is_completed ? 'Completed' : 'Pending';
        return `
          <div class="task-block">
            <div class="task-header">Task ${idx + 1}</div>
            <h2 class="task-title">${esc(t.title)}</h2>
            <p><strong>Description:</strong> <span>${esc(t.description)}</span></p>
            <p><strong>Due Date:</strong> <span>${fmtDate(t.due_date)}</span></p>
            <p><strong>Status:</strong> <span>${status}</span></p>
            <p><strong>Created:</strong> <span>${fmtDate(t.created_at)}</span></p>
            <p><strong>Updated:</strong> <span>${fmtDate(t.updated_at)}</span></p>
          </div>
        `;
      }).join('');

      const docContent = `
        <html>
          <head>
            <meta charset="UTF-8" />
            <style>
              body { font-family: Arial, sans-serif; color: #111; margin: 24px; }
              h1 { margin-bottom: 4px; }
              .meta { margin: 0 0 16px 0; color: #444; }
              .task-block { border: 1px solid #ccc; border-radius: 10px; padding: 14px 16px; margin-bottom: 16px; }
              .task-header { font-weight: 700; color: #555; margin-bottom: 6px; }
              .task-title { margin: 4px 0 10px; font-size: 18px; font-weight: 700; }
              p { margin: 4px 0; }
              strong { font-weight: 700; }
            </style>
          </head>
          <body>
            <h1>Task Report</h1>
            <p class="meta">Generated: ${esc(new Date().toLocaleString())}</p>
            <p class="meta">Total Tasks: ${tasks.length}</p>
            ${tasksHtml || '<p>No tasks found.</p>'}
          </body>
        </html>
      `;

      const blob = new Blob([docContent], { type: 'application/msword' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().split('T')[0];
      a.download = `tasks_report_${stamp}.doc`; // .doc so Word opens neatly
      a.click();
      URL.revokeObjectURL(url);
      setSuccessMessage('Tasks exported as Word file');
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to export tasks';
      setValidationErrors(prev => ({ ...prev, general: errorMessage }));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="settings-container">
      <div className="tasks-header">
        <h3 className="list-title">ACCOUNT SETTINGS</h3>
      </div>

      <div className="settings-list">
        {/* Username Option */}
        <div className="settings-item">
          <div className="settings-info">
            <h4>Username</h4>
            <p>Change your public display name and login identity</p>
          </div>
          <button className="settings-action-btn" onClick={() => openModal("Username")}>Edit</button>
        </div>

        {/* Password Option */}
        <div className="settings-item">
          <div className="settings-info">
            <h4>Security</h4>
            <p>Update your password to keep your account secure</p>
          </div>
          <button className="settings-action-btn" onClick={() => openModal("Password")}>Update</button>
          
        </div>

        {/* Appearance Option */}
        <div className="settings-item appearance-item">
          <div className="settings-info">
            <h4>Appearance</h4>
            <p>Choose your preferred color theme</p>
          </div>
          <div className="theme-toggle">
            <button 
              className={`theme-btn ${theme === "dark" ? "active" : ""}`}
              onClick={() => handleThemeChange("dark")}
            >
              🌙 Dark
            </button>
            <button 
              className={`theme-btn ${theme === "light" ? "active" : ""}`}
              onClick={() => handleThemeChange("light")}
            >
              ☀️ Light
            </button>
          </div>
        </div>

        {/* Two-Factor Authentication Option */}
        <div className="settings-item">
          <div className="settings-info">
            <h4>Two-Factor Authentication</h4>
            <p>{twoFactorEnabled ? '2FA is enabled for your account' : 'Protect your account with email-based 2FA'}</p>
          </div>
          <div>
            <label className="switch">
              <input
                type="checkbox"
                checked={twoFactorEnabled}
                onChange={async (e) => {
                  const newVal = e.target.checked;
                  try {
                    await API.put('/settings/2fa', { enabled: newVal }, { headers: { Authorization: `Bearer ${token}` } });
                    setTwoFactorEnabled(newVal);
                    setSuccessMessage(newVal ? '2FA enabled' : '2FA disabled');
                    setTimeout(() => setSuccessMessage(''), 2000);
                  } catch (err) {
                    const errMsg = err.response?.data?.detail || 'Failed to update 2FA setting';
                    setValidationErrors(prev => ({ ...prev, general: errMsg }));
                  }
                }}
              />
              <span className="slider round"></span>
            </label>
          </div>
        </div>

        {/* Export Tasks Option */}
        <div className="settings-item">
          <div className="settings-info">
            <h4>Export Tasks</h4>
            <p>Download all task details in Word format</p>
          </div>
          <button className="settings-action-btn" onClick={handleDownloadAll} disabled={isExporting}>
            {isExporting ? 'Preparing...' : 'Download'}
          </button>
        </div>

        {/* Delete Account Option */}
        <div className="settings-item">
          <div className="settings-info">
            <h4>Delete Your Account</h4>
            <p>Permanently delete your account and associated tasks</p>
          </div>
          <button className="settings-action-btn" onClick={() => openModal("Delete")}>Delete</button>
        </div>
      </div>
      

      {/* --- ULTRA GLASS SETTINGS MODAL --- */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className={`modal-content ultra-glass ${modalType === "Delete" ? "danger-mode" : ""}`} onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={closeModal}>✕</button>
            <h3 className="modal-title">{modalType === "Delete" ? "Delete Account" : `Update ${modalType}`}</h3>
            
            <form onSubmit={handleUpdate} className="add-task-form-popup">
              {modalType === "Username" ? (
                <div className="form-group">
                  <label>New Username</label>
                  <input 
                    type="text" 
                    placeholder="Enter new username" 
                    value={formData.username}
                    onChange={(e) => handleInputChange("username", e.target.value)}
                    required 
                  />
                    {validationErrors.username && (
                      <p className="validation-error" style={{ 
                        color: document.documentElement.getAttribute('data-theme') === 'light' ? '#ff8a80' : '#ff6b6b', 
                        fontSize: '12px', 
                        marginTop: '5px',
                        fontWeight: '500'
                      }}>
                      {validationErrors.username}
                    </p>
                  )}
                </div>
              ) : modalType === "Password" ? (
                <>
                  <div className="form-group">
                    <label>Current Password</label>
                    <input 
                      type="password" 
                      placeholder="Enter current password" 
                      value={formData.currentPassword}
                      onChange={(e) => handleInputChange("currentPassword", e.target.value)}
                      required 
                    />
                    {validationErrors.currentPassword && (
                      <p className="validation-error" style={{ 
                        color: document.documentElement.getAttribute('data-theme') === 'light' ? '#ff8a80' : '#ff6b6b', 
                        fontSize: '12px', 
                        marginTop: '5px',
                        fontWeight: '500'
                      }}>
                        {validationErrors.currentPassword}
                      </p>
                    )}
                  </div>
                  <div className="form-group">
                    <label>New Password</label>
                    <input 
                      type="password" 
                      placeholder="Enter new password" 
                      value={formData.newPassword}
                      onChange={(e) => handleInputChange("newPassword", e.target.value)}
                      required 
                    />
                    {validationErrors.newPassword && (
                      <p className="validation-error" style={{ 
                        color: document.documentElement.getAttribute('data-theme') === 'light' ? '#ff8a80' : '#ff6b6b', 
                        fontSize: '12px', 
                        marginTop: '5px',
                        fontWeight: '500'
                      }}>
                        {validationErrors.newPassword}
                      </p>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Confirm New Password</label>
                    <input 
                      type="password" 
                      placeholder="Confirm new password" 
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                      required 
                    />
                    {validationErrors.confirmPassword && (
                      <p className="validation-error" style={{ 
                        color: document.documentElement.getAttribute('data-theme') === 'light' ? '#ff8a80' : '#ff6b6b', 
                        fontSize: '12px', 
                        marginTop: '5px',
                        fontWeight: '500'
                      }}>
                        {validationErrors.confirmPassword}
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <p className="danger-text">
                    This will schedule your account for deletion in 24 hours. Log in within that window to cancel.
                  </p>
                  <div className="form-group">
                    <label>Confirm with Password</label>
                    <input 
                      type="password" 
                      placeholder="Enter your current password" 
                      value={formData.currentPassword}
                      onChange={(e) => handleInputChange("currentPassword", e.target.value)}
                      required 
                    />
                    {validationErrors.currentPassword && (
                      <p className="validation-error" style={{ 
                        color: document.documentElement.getAttribute('data-theme') === 'light' ? '#ff8a80' : '#ff6b6b', 
                        fontSize: '12px', 
                        marginTop: '5px',
                        fontWeight: '500'
                      }}>
                        {validationErrors.currentPassword}
                      </p>
                    )}
                  </div>
                </>
              )}          
              {validationErrors.general && (
                <p className="validation-error" style={{ 
                  color: document.documentElement.getAttribute('data-theme') === 'light' ? '#ff8a80' : '#ff6b6b', 
                  fontSize: '12px', 
                  marginTop: '10px',
                  fontWeight: '500',
                  textAlign: 'center'
                }}>
                  {validationErrors.general}
                </p>
              )}
              {successMessage && (
                <p style={{ 
                  color: '#4ade80', 
                  fontSize: '12px', 
                  marginTop: '10px',
                  fontWeight: '500',
                  textAlign: 'center'
                }}>
                  {successMessage}
                </p>
              )}
              <button 
                type="submit" 
                className="save-btn"
                disabled={isUpdating || Object.entries(validationErrors).some(([key, error]) => key !== "general" && error !== "")}
                style={{
                  opacity: (isUpdating || Object.entries(validationErrors).some(([key, error]) => key !== "general" && error !== "")) ? 0.5 : 1,
                  cursor: (isUpdating || Object.entries(validationErrors).some(([key, error]) => key !== "general" && error !== "")) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px'
                }}
              >
                {isUpdating ? (
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    borderTop: '2px solid #fff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                ) : (
                  modalType === "Delete" ? "Delete Account" : "Save Changes"
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {isConfirmOpen && (
        <div className="confirm-overlay">
          <div className="confirm-box">
            <h4>Are you sure?</h4>
            <p>Your account will be permanently deleted after 24 hours</p>
            <div className="confirm-actions">
              <button
                type="button"
                className="confirm-btn cancel"
                onClick={() => {
                  setIsConfirmOpen(false);
                  setIsUpdating(false);
                }}
              >
                No
              </button>
              <button
                type="button"
                className="confirm-btn danger"
                onClick={handleConfirmDelete}
                disabled={isUpdating}
              >
                {isUpdating ? "Deleting..." : "Yes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    
  );
};

export default Settings;